/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * aui-component 재연결 시 [data-is-lazy-connect] 대상의 자식 DOM 을
 * 프레임 예산 내에서 순차적으로 재연결하는 클래스를 정의한다.
 *
 * @file /src/utils/Connector.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 23.
 */
import { AnimationFrames } from '@utils/AnimationFrames';

export class Connector {
    private static queue: Map<Element, DocumentFragment> = new Map();
    private static stored: WeakMap<Element, DocumentFragment> = new WeakMap();
    private static resolvers: Map<Element, (() => void)[]> = new Map();
    private static rAFId: string = null;
    private static pendingStart: boolean = false;
    private static readonly FRAME_BUDGET = 8;

    private constructor() {}

    /**
     * aui-component 경계를 넘지 않고 [data-is-lazy-connect] 요소가 존재하는지 확인한다.
     *
     * @param root - 탐색을 시작할 요소
     * @returns 존재 여부
     */
    static hasTargets(root: Element): boolean {
        const walk = (el: Element): boolean => {
            for (const child of el.children) {
                if (child.tagName === 'AUI-COMPONENT') continue;
                if (child.hasAttribute('data-is-lazy-connect') == true) return true;
                if (walk(child) == true) return true;
            }
            return false;
        };
        return walk(root);
    }

    /**
     * root 아래의 [data-is-lazy-connect] 요소 중 복원 대기 중인 stored fragment 가 있는지 확인한다.
     *
     * @param root - 탐색을 시작할 요소
     * @returns 복원 대기 fragment 존재 여부
     */
    static hasStored(root: Element): boolean {
        const walk = (el: Element): boolean => {
            for (const child of el.children) {
                if (child.tagName === 'AUI-COMPONENT') continue;
                if (
                    child.hasAttribute('data-is-lazy-connect') == true &&
                    (this.stored.has(child) == true || this.queue.has(child) == true)
                )
                    return true;
                if (walk(child) == true) return true;
            }
            return false;
        };
        return walk(root);
    }

    /**
     * aui-component 경계를 넘지 않고 [data-is-lazy-connect] 요소를 탐색한다.
     *
     * @param root - 탐색을 시작할 요소
     * @returns 탐색된 요소 목록
     */
    static findTargets(root: Element): Element[] {
        const results: Element[] = [];
        const walk = (el: Element) => {
            for (const child of el.children) {
                if (child.tagName === 'AUI-COMPONENT') continue;
                if (child.hasAttribute('data-is-lazy-connect') == true) results.push(child);
                walk(child);
            }
        };
        walk(root);
        return results;
    }

    /**
     * 대상 요소의 자식 DOM 을 보관한다.
     * 재연결 진행 중인 작업이 있으면 취소하고 이미 재연결된 자식과 병합한다.
     *
     * @param target - 보관할 대상 요소
     */
    static store(target: Element): void {
        const pending = this.cancel(target);

        let extracted: DocumentFragment | null = null;
        if (target.childNodes.length > 0) {
            const range = new Range();
            range.selectNodeContents(target);
            extracted = range.extractContents();
        }

        if (extracted !== null && pending !== null) {
            extracted.append(pending);
            this.stored.set(target, extracted);
        } else if (extracted !== null) {
            this.stored.set(target, extracted);
        } else if (pending !== null) {
            this.stored.set(target, pending);
        }
    }

    /**
     * 보관된 자식 DOM 을 대상 요소에 순차 재연결 대기열에 등록하고,
     * 재연결이 완료되면 resolve 되는 Promise 를 반환한다.
     *
     * @param target - 재연결할 대상 요소
     * @returns 재연결 완료 Promise
     */
    static restore(target: Element): Promise<void> {
        const fragment = this.stored.get(target);
        if (fragment?.childNodes.length > 0) {
            this.stored.delete(target);
            return this.enqueue(target, fragment);
        }
        return Promise.resolve();
    }

    /**
     * root 아래의 모든 [data-is-lazy-connect] 요소를 재연결하고,
     * 전부 완료되면 resolve 되는 Promise 를 반환한다.
     *
     * @param root - 탐색을 시작할 요소
     * @returns 전체 재연결 완료 Promise
     */
    static restoreAll(root: Element): Promise<void> {
        const targets = this.findTargets(root);
        if (targets.length == 0) {
            return Promise.resolve();
        }
        return Promise.all(targets.map((t) => this.restore(t))).then(() => {});
    }

    /**
     * 대상 요소의 fragment 를 재연결 대기열에 추가하고 완료 Promise 를 반환한다.
     *
     * @param target - 재연결할 대상 요소
     * @param fragment - 재연결할 자식 DOM fragment
     * @returns 재연결 완료 Promise
     */
    private static enqueue(target: Element, fragment: DocumentFragment): Promise<void> {
        return new Promise((resolve) => {
            const existing = this.queue.get(target);
            if (existing !== undefined) {
                existing.append(fragment);
            } else {
                this.queue.set(target, fragment);
            }

            const callbacks = this.resolvers.get(target) ?? [];
            callbacks.push(resolve);
            this.resolvers.set(target, callbacks);

            if ((this.rAFId === null || AnimationFrames.isActive(this.rAFId) === false) && this.pendingStart == false) {
                this.pendingStart = true;
                requestAnimationFrame(() => {
                    this.pendingStart = false;
                    this.rAFId = AnimationFrames.request(this.tick, 'Connector', this);
                });
            }
        });
    }

    /**
     * 진행 중인 재연결 작업을 취소하고 남은 fragment 를 반환한다.
     * 대기 중인 Promise 들은 취소 시점에 resolve 한다.
     *
     * @param target - 취소할 대상 요소
     * @returns 남은 fragment (없으면 null)
     */
    private static cancel(target: Element): DocumentFragment | null {
        const fragment = this.queue.get(target) ?? null;
        this.queue.delete(target);

        const callbacks = this.resolvers.get(target) ?? [];
        this.resolvers.delete(target);
        callbacks.forEach((cb) => cb());

        return fragment;
    }

    /**
     * 프레임 예산 내에서 재연결 작업을 처리한다.
     *
     * @param frameStart - 프레임 시작 시간
     * @returns 작업이 남아있으면 true
     */
    private static tick(frameStart: DOMHighResTimeStamp): boolean {
        const deadline = frameStart + this.FRAME_BUDGET;

        for (const [target, fragment] of this.queue) {
            if (performance.now() >= deadline) break;

            if (target.isConnected == false) {
                this.queue.delete(target);
                const callbacks = this.resolvers.get(target) ?? [];
                this.resolvers.delete(target);
                callbacks.forEach((cb) => cb());
                continue;
            }

            while (fragment.childNodes.length > 0 && performance.now() < deadline) {
                target.appendChild(fragment.firstChild);
            }

            if (fragment.childNodes.length === 0) {
                this.queue.delete(target);
                const callbacks = this.resolvers.get(target) ?? [];
                this.resolvers.delete(target);
                callbacks.forEach((cb) => cb());
            }
        }

        if (this.queue.size > 0) return true;

        AnimationFrames.cancel(this.rAFId, true);
        this.rAFId = null;
        return false;
    }
}
